import styled from 'styled-components';

import { NodeField } from '@components/Flow/Nodes';
import { Command, tabActions } from '@store/slices';
import { useEffect, useState } from 'react';
import { Button } from '@common/Button';
import { Alert, IconButton, Stack, TextField } from '@mui/material';
import { Tooltip } from '@common/Tooltip';
import { useTheme } from '../../../../styles';
import { v4 as uuidv4 } from 'uuid';
import { useDispatch } from 'react-redux';
import { DeleteOutline, Person2Outlined, SmartToyOutlined, TerminalOutlined } from '@mui/icons-material';

const Container = styled.div`
  color: ${({ theme }) => theme.text.standard};

  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const MessageContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 12px;
  flex-direction: column;
  margin-top: 0.5rem;
`;

const MessageInputContainer = styled.div<{ noMessages?: boolean }>`
  display: flex;
  width: 100%;
  gap: 8px;
  margin-top: ${({ noMessages }) => (noMessages ? 0 : '2.5rem')};
`;

export type Message = {
  id: string;
  role: string;
  content: string;
};

interface Props {
  command: Command;
  field: NodeField;
}

const getStoredSystem = (command: Command, field: NodeField) => {
  try {
    const initialMessages = command.state?.[field.id]?.messages || [];
    const systemMessage = initialMessages.find((message: Message) => message.role === 'system');
    return systemMessage ? systemMessage.content : '';
  } catch (e) {
    return [];
  }
};

const getStoredMessages = (command: Command, field: NodeField) => {
  try {
    const initialMessages = command.state?.[field.id]?.messages || [];
    return initialMessages.filter((message: Message) => message.role !== 'system');
  } catch (e) {
    return [];
  }
};

export const NodeMessageArray: React.FC<Props> = ({ field, command }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const initialSystem = getStoredSystem(command, field);
  const initialMessages = getStoredMessages(command, field);

  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [system, setSystem] = useState<string>(initialSystem);
  const [value, setValue] = useState<string>('');
  const [role, setRole] = useState<'User' | 'Assistant'>('User');

  useEffect(() => {
    setMessages(initialMessages);
    setSystem(initialSystem);
  }, [command.node.id]);

  const updateFieldValue = (messageArray: Message[]) => {
    const nodeId = command.node.id;
    const fieldId = field.id;

    dispatch(tabActions.nodeInputUpdate({ nodeId, fieldId, fieldName: 'messages', value: messageArray }));
    dispatch(tabActions.setSaved(false));
    try {
    } catch (e) {
      console.log('Failed to update message array', e);
    }
  };

  const addMessage = () => {
    const message = { id: uuidv4(), role: role.toLowerCase(), content: value };
    setValue('');

    const messageArray = [{ id: '', role: 'system', content: system }, ...messages, message];

    setMessages((prevState) => prevState.concat(message));
    updateFieldValue(messageArray);
    changeRole();
  };

  const updateMessage = (id: string, content: string) => {
    const messageArray = [
      { id: 'system', role: 'system', content: system },
      ...messages.map((message) => {
        if (message.id === id) {
          return { ...message, content };
        }
        return message;
      }),
    ];

    setMessages((prevState) =>
      prevState.map((message) => {
        if (message.id === id) {
          return { ...message, content };
        }
        return message;
      })
    );
    updateFieldValue(messageArray);
  };

  const deleteMessage = (id: string) => {
    const messageArray = [
      { id: 'system', role: 'system', content: system },
      ...messages.filter((item) => item.id !== id),
    ];

    setMessages((prevState) => prevState.filter((item) => item.id !== id));
    updateFieldValue(messageArray);
  };

  const toggleMessageRole = (id: string) => {
    const messageArray = [
      { id: 'system', role: 'system', content: system },
      ...messages.map((message) => {
        if (message.id === id) {
          return { ...message, role: message.role === 'user' ? 'assistant' : 'user' };
        }
        return message;
      }),
    ];

    setMessages((prevState) =>
      prevState.map((message) => {
        if (message.id === id) {
          return { ...message, role: message.role === 'user' ? 'assistant' : 'user' };
        }
        return message;
      })
    );

    updateFieldValue(messageArray);
  };

  const changeSystemMessage = (value: string) => {
    const messageArray = [{ id: 'system', role: 'system', content: value }, ...messages];

    setSystem(value);
    updateFieldValue(messageArray);
  };

  const changeRole = () => {
    if (role === 'User') setRole('Assistant');
    else setRole('User');
  };

  return (
    <Container>
      <MessageContainer>
        <Stack spacing={1.25} direction='row' width='100%' display='flex' alignItems='center'>
          <Tooltip title='The system message to be used' placement='left' arrow>
            <IconButton size='small'>
              <TerminalOutlined sx={{ fontSize: '2.1ch' }} />
            </IconButton>
          </Tooltip>
          <TextField
            size='small'
            value={system}
            onChange={(e) => changeSystemMessage(e.target.value)}
            fullWidth
            multiline
            minRows={1}
            maxRows={8}
            placeholder={'System Message'}
          />
        </Stack>
        {messages.length === 0 && (
          <Alert severity='info'>
            Give your bot a system message and use the button below to add conversation history. You can click the icon
            next to the message to change the role of the message.
          </Alert>
        )}
        {messages.map((message, index) => (
          <Stack spacing={1.25} direction='row' width='100%' display='flex' alignItems='center' key={index}>
            <Tooltip
              title={`This message is from the ${message.role}. Click to change to ${
                message.role === 'user' ? 'assistant' : 'user'
              }.`}
              placement='left'
              arrow
            >
              <IconButton size='small' onClick={() => toggleMessageRole(message.id)}>
                {message.role === 'assistant' ? (
                  <Stack spacing={0.1} alignItems='center' justifyContent='center' sx={{ minWidth: '22px' }}>
                    <SmartToyOutlined sx={{ fontSize: '1.9ch', color: theme.telnyx.yellow }} />
                    <p style={{ fontSize: '11px' }}>Bot</p>
                  </Stack>
                ) : (
                  <Stack spacing={0.1} alignItems='center' justifyContent='center' sx={{ minWidth: '22px' }}>
                    <Person2Outlined sx={{ fontSize: '2ch' }} />
                    <p style={{ fontSize: '11px' }}>User</p>
                  </Stack>
                )}
              </IconButton>
            </Tooltip>
            <TextField
              size='small'
              value={message.content}
              onChange={(e) => updateMessage(message.id, e.target.value)}
              fullWidth
              multiline
              minRows={1}
              maxRows={8}
              placeholder={message.role === 'user' ? 'User Message' : 'Assistant Message'}
            />
            <IconButton size='small' onClick={() => deleteMessage(message.id)}>
              <DeleteOutline sx={{ color: `${theme.text.subtext} !important` }} />
            </IconButton>
          </Stack>
        ))}
      </MessageContainer>
      <MessageInputContainer noMessages={true}>
        <Button onClick={addMessage} fullWidth customColor='blue'>
          Add Message
        </Button>
      </MessageInputContainer>
    </Container>
  );
};
